import React from 'react';
import { 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  Upload, 
  ChevronRight,
  Shield,
  Globe,
  TrendingDown,
  Clock,
  Zap
} from 'lucide-react';

// Types
interface CriticalCheck {
  name: string;
  status: 'PASS' | 'FAIL' | 'N/A' | 'WARNING' | 'PENDING';
  critical: boolean;
  detail: string;
  [key: string]: any;
}

interface ValidationCheck {
  name: string;
  status: 'PASS' | 'FAIL' | 'PENDING' | 'WARNING';
  critical: boolean;
  detail: string;
  [key: string]: any;
}

interface Warning {
  type: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  recommendation: string;
  impact?: string;
  [key: string]: any;
}

interface Recommendation {
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  action: string;
  reason: string;
  benefit: string;
  cta: string;
  icon: string;
}

interface HealthAssessment {
  status: 'GREEN' | 'YELLOW' | 'RED';
  status_message: string;
  status_emoji: string;
  status_color: string;
  critical: CriticalCheck[];
  validation: ValidationCheck[];
  warnings: Warning[];
  recommendations: Recommendation[];
  summary: {
    critical_passed: string;
    validation_passed: string;
    warnings_count: number;
    recommendations_count: number;
    high_priority_count: number;
  };
  client_name?: string;
  business_type?: string;
  final_claim_amount?: number;
}

// Status Badge Component
export const ClaimHealthBadge: React.FC<{ 
  status: string; 
  message?: string;
  compact?: boolean;
}> = ({ status, message, compact = false }) => {
  const config = {
    GREEN: { 
      bg: 'bg-green-100', 
      text: 'text-green-700', 
      border: 'border-green-300',
      label: 'Ready' 
    },
    YELLOW: { 
      bg: 'bg-yellow-100', 
      text: 'text-yellow-700', 
      border: 'border-yellow-300',
      label: 'Needs Review' 
    },
    RED: { 
      bg: 'bg-red-100', 
      text: 'text-red-700', 
      border: 'border-red-300',
      label: 'Incomplete' 
    }
  }[status] || { 
    bg: 'bg-gray-100', 
    text: 'text-gray-700', 
    border: 'border-gray-300',
    label: 'Unknown' 
  };

  const emoji = status === 'GREEN' ? '🟢' : status === 'YELLOW' ? '🟡' : '🔴';

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <span>{emoji}</span>
        <span>{config.label}</span>
      </span>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${config.bg} ${config.border}`}>
      <span className="text-xl">{emoji}</span>
      <div>
        <span className={`font-bold ${config.text}`}>{config.label}</span>
        {message && <p className="text-xs text-gray-600">{message}</p>}
      </div>
    </div>
  );
};

// Status Icon Component
const StatusIcon: React.FC<{ status: string; className?: string }> = ({ status, className = 'w-5 h-5' }) => {
  switch (status) {
    case 'PASS':
      return <CheckCircle className={`${className} text-green-500`} />;
    case 'FAIL':
      return <XCircle className={`${className} text-red-500`} />;
    case 'WARNING':
      return <AlertCircle className={`${className} text-yellow-500`} />;
    case 'PENDING':
      return <Clock className={`${className} text-gray-400`} />;
    case 'N/A':
      return <span className={`${className} text-gray-400`}>—</span>;
    default:
      return <AlertCircle className={`${className} text-gray-400`} />;
  }
};

// Critical Requirements Section
const CriticalRequirements: React.FC<{ checks: CriticalCheck[] }> = ({ checks }) => {
  const passCount = checks.filter(c => c.status === 'PASS').length;
  const totalRequired = checks.filter(c => c.status !== 'N/A').length;

  return (
    <div className="bg-white rounded-lg border p-4">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Shield className="w-5 h-5 text-blue-600" />
        Critical Requirements
        <span className="text-sm font-normal text-gray-500">
          ({passCount}/{totalRequired} passed)
        </span>
      </h4>
      <div className="space-y-2">
        {checks.map((check, idx) => (
          <div 
            key={idx} 
            className={`flex items-center justify-between p-3 rounded-lg ${
              check.status === 'PASS' ? 'bg-green-50' :
              check.status === 'FAIL' ? 'bg-red-50' :
              check.status === 'WARNING' ? 'bg-yellow-50' :
              'bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <StatusIcon status={check.status} />
              <div>
                <p className="font-medium text-gray-900">{check.name}</p>
                <p className="text-sm text-gray-600">{check.detail}</p>
              </div>
            </div>
            {check.status === 'FAIL' && (
              <button className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1">
                <Upload className="w-4 h-4" />
                Upload
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Validation Checks Section
const ValidationChecks: React.FC<{ checks: ValidationCheck[] }> = ({ checks }) => {
  return (
    <div className="bg-white rounded-lg border p-4">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <CheckCircle className="w-5 h-5 text-green-600" />
        Validation Checks
      </h4>
      <div className="space-y-2">
        {checks.map((check, idx) => (
          <div 
            key={idx} 
            className={`flex items-center justify-between p-3 rounded-lg ${
              check.status === 'PASS' ? 'bg-green-50' :
              check.status === 'FAIL' ? 'bg-red-50' :
              check.status === 'PENDING' ? 'bg-gray-50' :
              'bg-yellow-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <StatusIcon status={check.status} />
              <div>
                <p className="font-medium text-gray-900">{check.name}</p>
                <p className="text-sm text-gray-600">{check.detail}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Warnings Section
const WarningsSection: React.FC<{ warnings: Warning[] }> = ({ warnings }) => {
  if (warnings.length === 0) return null;

  return (
    <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
      <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        Warnings for Attorney/Adjuster
        <span className="text-sm font-normal">({warnings.length})</span>
      </h4>
      <div className="space-y-3">
        {warnings.map((warning, idx) => (
          <div 
            key={idx} 
            className={`p-4 rounded-lg border-l-4 ${
              warning.severity === 'high' ? 'bg-red-50 border-red-500' :
              warning.severity === 'medium' ? 'bg-yellow-50 border-yellow-500' :
              'bg-blue-50 border-blue-500'
            }`}
          >
            <div className="flex items-start gap-2">
              {warning.type === 'pre_event_decline' && <TrendingDown className="w-5 h-5 text-orange-600 mt-0.5" />}
              {warning.type === 'timing_mismatch' && <Clock className="w-5 h-5 text-orange-600 mt-0.5" />}
              {warning.type === 'rapid_recovery' && <Zap className="w-5 h-5 text-orange-600 mt-0.5" />}
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{warning.title}</p>
                <p className="text-sm text-gray-700 mt-1">{warning.message}</p>
                {warning.recommendation && (
                  <p className="text-sm text-blue-700 mt-2 flex items-center gap-1">
                    <ChevronRight className="w-4 h-4" />
                    {warning.recommendation}
                  </p>
                )}
                {warning.impact && (
                  <p className="text-xs text-gray-500 mt-1 italic">{warning.impact}</p>
                )}
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${
                warning.severity === 'high' ? 'bg-red-100 text-red-700' :
                warning.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {warning.severity.toUpperCase()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Recommendations Section
const RecommendationsSection: React.FC<{ 
  recommendations: Recommendation[];
  onAction?: (cta: string) => void;
}> = ({ recommendations, onAction }) => {
  if (recommendations.length === 0) return null;

  const groupedRecs = {
    HIGH: recommendations.filter(r => r.priority === 'HIGH'),
    MEDIUM: recommendations.filter(r => r.priority === 'MEDIUM'),
    LOW: recommendations.filter(r => r.priority === 'LOW')
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-blue-600" />
        Recommendations
      </h4>

      {/* HIGH Priority */}
      {groupedRecs.HIGH.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-red-600 uppercase mb-2">🔴 High Priority</p>
          <div className="space-y-2">
            {groupedRecs.HIGH.map((rec, idx) => (
              <div key={idx} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{rec.action}</p>
                    <p className="text-sm text-gray-600 mt-1">{rec.reason}</p>
                    <p className="text-sm text-green-600 mt-1 font-medium">
                      ✓ {rec.benefit}
                    </p>
                  </div>
                  {onAction && rec.cta && (
                    <button 
                      onClick={() => onAction(rec.cta)}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 flex items-center gap-1"
                    >
                      {rec.cta.includes('upload') ? <Upload className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      {rec.cta.includes('upload') ? 'Upload' : 'Fix'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MEDIUM Priority */}
      {groupedRecs.MEDIUM.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-yellow-600 uppercase mb-2">🟡 Suggested</p>
          <div className="space-y-2">
            {groupedRecs.MEDIUM.map((rec, idx) => (
              <div key={idx} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="font-semibold text-gray-900">{rec.action}</p>
                <p className="text-sm text-gray-600 mt-1">{rec.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LOW Priority */}
      {groupedRecs.LOW.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-blue-600 uppercase mb-2">🟢 Optional Enhancements</p>
          <div className="space-y-2">
            {groupedRecs.LOW.map((rec, idx) => (
              <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-gray-900 text-sm">{rec.action}</p>
                <p className="text-xs text-gray-600 mt-1">{rec.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Traffic Light Health Card Component
export const ClaimHealthCard: React.FC<{
  assessment: HealthAssessment;
  compact?: boolean;
  onAction?: (cta: string) => void;
}> = ({ assessment, compact = false, onAction }) => {
  const { status, status_message, critical, validation, warnings, recommendations, summary } = assessment;

  // Compact version for dashboard cards
  if (compact) {
    return (
      <div className={`rounded-lg border-l-4 p-4 ${
        status === 'GREEN' ? 'border-green-500 bg-green-50' :
        status === 'YELLOW' ? 'border-yellow-500 bg-yellow-50' :
        'border-red-500 bg-red-50'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {status === 'GREEN' ? '🟢' : status === 'YELLOW' ? '🟡' : '🔴'}
            </span>
            <div>
              <p className="font-semibold text-gray-900">
                {status === 'GREEN' ? 'Ready' : status === 'YELLOW' ? 'Needs Review' : 'Incomplete'}
              </p>
              <p className="text-sm text-gray-600">{status_message}</p>
            </div>
          </div>
          {summary && summary.high_priority_count > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
              {summary.high_priority_count} action{summary.high_priority_count > 1 ? 's' : ''} needed
            </span>
          )}
        </div>
        {warnings.length > 0 && (
          <p className="text-sm text-yellow-700 mt-2 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            {warnings.length} warning{warnings.length > 1 ? 's' : ''} for review
          </p>
        )}
      </div>
    );
  }

  // Full detailed view
  return (
    <div className="bg-gray-50 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className={`p-6 ${
        status === 'GREEN' ? 'bg-green-600' :
        status === 'YELLOW' ? 'bg-yellow-500' :
        'bg-red-600'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-5xl">
              {status === 'GREEN' ? '🟢' : status === 'YELLOW' ? '🟡' : '🔴'}
            </span>
            <div className="text-white">
              <h2 className="text-2xl font-bold">
                CLAIM HEALTH: {status === 'GREEN' ? 'READY' : status === 'YELLOW' ? 'NEEDS REVIEW' : 'INCOMPLETE'}
              </h2>
              <p className="text-white/90 mt-1">{status_message}</p>
            </div>
          </div>
          {summary && (
            <div className="text-white text-right">
              <p className="text-sm opacity-90">Requirements: {summary.critical_passed}</p>
              <p className="text-sm opacity-90">Validation: {summary.validation_passed}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Critical Requirements */}
        <CriticalRequirements checks={critical} />

        {/* Validation Checks */}
        <ValidationChecks checks={validation} />

        {/* Warnings */}
        <WarningsSection warnings={warnings} />

        {/* Recommendations */}
        <RecommendationsSection recommendations={recommendations} onAction={onAction} />
      </div>
    </div>
  );
};

export default ClaimHealthCard;
